/**
 * La pantalla donde se juega (MVP §6): cabecera, rejilla, reparto, pistas, reglas y
 * acciones.
 *
 * Todo el estado de la partida vive en un `useReducer` sobre `caseReducer`, y su parte
 * serializable (`state.progress`) es exactamente lo que MON-61 tendrá que guardar en
 * `localStorage`: no hace falta refactorizar nada para persistirla.
 */

import { useMemo, useReducer, useState } from 'react';

import { CaseGrid } from './CaseGrid';
import { CaseHeader } from './CaseHeader';
import { CaseToolbar } from './CaseToolbar';
import { CastPanel } from './CastPanel';
import { CluesPanel } from './CluesPanel';
import { clueFocus, focusKeys } from './clueFocus';
import { RulesPanel } from './RulesPanel';
import {
  canRedo,
  canUndo,
  caseReducer,
  cellPerson,
  conflictCount,
  createCaseState,
  filledCount,
  isComplete,
} from './state';
import type { LoadedCase } from './useCase';

export function CasePage({ loaded }: { loaded: LoadedCase }) {
  const { caso, text, rules, rooms, people, descriptors } = loaded;
  const [state, dispatch] = useReducer(caseReducer, caso.n, createCaseState);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const highlighted = useMemo(() => {
    const clue = caso.clues.find((candidate) => candidate.id === highlightedId);
    return focusKeys(clue === undefined ? null : clueFocus(clue, caso.n));
  }, [caso, highlightedId]);

  const conflicts = conflictCount(state);
  const filled = filledCount(state);
  const complete = isComplete(state);
  const accused = cellPerson(state, caso.crime.hour, caso.crime.room);

  const crimeRoom = rooms[caso.crime.room];
  const crimeHour = caso.hours[caso.crime.hour];
  if (crimeRoom === undefined || crimeHour === undefined) {
    throw new Error('el caso apunta a una sala u hora del crimen inexistente');
  }

  return (
    <main className="mx-auto flex max-w-5xl flex-col gap-8 px-3 py-8 sm:px-6">
      <CaseHeader
        title={text.title}
        opening={text.opening}
        difficulty={caso.difficulty}
        crimeRoom={crimeRoom}
        crimeHour={crimeHour}
      />

      <div className="flex flex-col gap-4">
        <CaseToolbar
          canUndo={canUndo(state)}
          canRedo={canRedo(state)}
          errors={state.progress.errors}
          accused={accused === null ? null : (people[accused]?.name ?? null)}
          onUndo={() => dispatch({ type: 'undo' })}
          onRedo={() => dispatch({ type: 'redo' })}
          onClearGrid={() => dispatch({ type: 'clearGrid' })}
        />

        <CaseGrid
          state={state}
          people={people}
          hours={caso.hours}
          rooms={rooms}
          crime={caso.crime}
          highlighted={highlighted}
          onPlace={(hour, room, person) =>
            dispatch({ type: 'place', hour, room, person })
          }
          onClear={(hour, room) => dispatch({ type: 'clearCell', hour, room })}
          onToggleNote={(hour, room, person) =>
            dispatch({ type: 'toggleNote', hour, room, person })
          }
        />

        {/* Estado de la rejilla para quien no ve los bordes rojos. */}
        <p role="status" aria-live="polite" className="text-text-muted text-sm">
          {complete
            ? 'Rejilla completa y sin conflictos.'
            : `${filled} de ${caso.n * caso.n} casillas rellenas · ${
                conflicts === 0
                  ? 'sin conflictos'
                  : `${conflicts} ${conflicts === 1 ? 'casilla en conflicto' : 'casillas en conflicto'}`
              }`}
        </p>
      </div>

      <CastPanel people={people} descriptors={descriptors} />

      <div className="grid gap-8 lg:grid-cols-[1fr_18rem]">
        <CluesPanel
          clues={text.clues}
          struck={state.progress.struck}
          highlightedId={highlightedId}
          onToggle={(id) => {
            dispatch({ type: 'toggleClue', id });
            setHighlightedId((current) => (current === id ? null : id));
          }}
        />
        <RulesPanel rules={rules} />
      </div>
    </main>
  );
}
